// client/src/components/CookieConsent.jsx
import React, { useState, useEffect } from 'react';
import { X, Settings, Cookie } from 'lucide-react';

const CookieConsent = () => {
    const [showBanner, setShowBanner] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [analyticsConsent, setAnalyticsConsent] = useState(true);

    // Cookie utility functions
    const setCookie = (name, value, days) => {
        const d = new Date();
        d.setTime(d.getTime() + (days * 24 * 60 * 60 * 1000));
        const expires = "expires=" + d.toUTCString();
        document.cookie = name + "=" + value + ";" + expires + ";path=/;SameSite=Lax";
    };

    const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
    };

    // Load Google Analytics
    const loadGoogleAnalytics = () => {
        if (window.gtag) return;

        const script = document.createElement('script');
        script.async = true;
        const gaId = process.env.REACT_APP_GOOGLE_ANALYTICS_ID;
        if (!gaId) return;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`
        document.head.appendChild(script);

        script.onload = function() {
            window.dataLayer = window.dataLayer || [];
            function gtag(){window.dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', gaId, {
                anonymize_ip: true,
                cookie_flags: 'SameSite=Lax;Secure'
            });
            console.log('Google Analytics loaded');
        };
    };

    // Load Microsoft Clarity
    const loadClarity = () => {
        if (window.clarity) return;
        const clarityId = process.env.REACT_APP_MICROSOFT_CLARITY_ID;
        if (!clarityId) return;

        // Alternative loading method that bypasses COEP issues
        const clarityScript = `
       (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "${clarityId}");
    `;

        const script = document.createElement('script');
        script.innerHTML = clarityScript;
        document.head.appendChild(script);
    };

    // Initialize analytics based on consent
    const initializeAnalytics = () => {
        const consent = getCookie('cookie-consent');
        const analyticsConsent = getCookie('analytics-consent');

        if (consent === 'accepted' || analyticsConsent === 'accepted') {
            if (process.env.REACT_APP_ENABLE_GOOGLE_ANALYTICS === 'true') {
                loadGoogleAnalytics();
            }
            if (process.env.REACT_APP_ENABLE_MICROSOFT_CLARITY === 'true') {
                loadClarity();
            }
        }
    };

    // Handle user's cookie choice
    const handleCookieChoice = (accept) => {
        if (accept) {
            setCookie('cookie-consent', 'accepted', 365);
            setCookie('analytics-consent', 'accepted', 365);
            loadGoogleAnalytics();
            loadClarity();
        } else {
            setCookie('cookie-consent', 'declined', 365);
            setCookie('analytics-consent', 'declined', 365);
        }
        setShowBanner(false);
    };

    // Show cookie settings modal
    const showCookieSettings = () => {
        setShowSettings(true);
        const analyticsConsent = getCookie('analytics-consent');
        setAnalyticsConsent(analyticsConsent === 'accepted');
    };

    // Save cookie settings from modal
    const saveCookieSettings = () => {
        setCookie('cookie-consent', 'custom', 365);

        if (analyticsConsent) {
            setCookie('analytics-consent', 'accepted', 365);
            loadGoogleAnalytics();
            loadClarity();
        } else {
            setCookie('analytics-consent', 'declined', 365);
            if (window.gtag) {
                window.gtag('consent', 'update', {
                    'analytics_storage': 'denied'
                });
            }
        }

        setShowBanner(false);
        setShowSettings(false);
    };

    // Check for existing consent on component mount
    useEffect(() => {
        const consent = getCookie('cookie-consent');
        if (!consent) {
            setTimeout(() => {
                setShowBanner(true);
            }, 1000);
        } else {
            initializeAnalytics();
        }
    }, []);

    // Make revoke function globally available
    useEffect(() => {
        window.revokeCookieConsent = () => {
            setCookie('cookie-consent', '', -1);
            setCookie('analytics-consent', '', -1);
            setShowBanner(true);

            if (window.gtag) {
                window.gtag('consent', 'update', {
                    'analytics_storage': 'denied'
                });
            }

            console.log('Cookie consent revoked');
        };
    }, []);

    if (!showBanner && !showSettings) return null;

    return (
        <>
            {/* Cookie Banner */}
            {showBanner && (
                <div className="fixed bottom-0 left-0 right-0 bg-black bg-opacity-90 text-white p-4 z-50 shadow-lg">
                    <div className="max-w-7xl mx-auto flex justify-between items-center flex-wrap gap-4">
                        <div className="flex-1 min-w-0">
                            <p className="text-sm mb-2">
                                We use cookies to improve your experience and analyze site usage.
                            </p>
                            <p className="text-xs opacity-80">
                                This includes Google Analytics for analytics purposes.
                            </p>
                        </div>
                        <div className="flex gap-2 flex-shrink-0 flex-wrap">
                            <button
                                onClick={showCookieSettings}
                                className="px-4 py-2 text-sm font-medium text-white bg-transparent border border-gray-500 rounded hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors duration-200"
                            >
                                <Settings className="w-4 h-4 inline mr-1" />
                                Settings
                            </button>
                            <button
                                onClick={() => handleCookieChoice(false)}
                                className="px-4 py-2 text-sm font-medium text-white bg-gray-700 rounded hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors duration-200"
                            >
                                Decline
                            </button>
                            <button
                                onClick={() => handleCookieChoice(true)}
                                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors duration-200"
                            >
                                Accept All
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Cookie Settings Modal */}
            {showSettings && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg max-w-md w-full max-h-[80vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <Cookie className="w-5 h-5 mr-2 text-orange-600" />
                                    Cookie Settings
                                </h3>
                                <button
                                    onClick={() => setShowSettings(false)}
                                    className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500 rounded"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <p className="text-gray-600 mb-6">
                                Choose which cookies you'd like to accept:
                            </p>

                            {/* Essential Cookies */}
                            <div className="border border-gray-200 rounded-lg p-4 mb-4">
                                <label className="flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={true}
                                        disabled
                                        className="mr-3 h-4 w-4 text-orange-600 rounded border-gray-300"
                                    />
                                    <span className="font-medium text-gray-900">Essential Cookies</span>
                                </label>
                                <p className="text-sm text-gray-600 mt-2 ml-7">
                                    Required for the website to function properly. Cannot be disabled.
                                </p>
                            </div>

                            {/* Analytics Cookies */}
                            <div className="border border-gray-200 rounded-lg p-4 mb-6">
                                <label className="flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={analyticsConsent}
                                        onChange={(e) => setAnalyticsConsent(e.target.checked)}
                                        className="mr-3 h-4 w-4 text-orange-600 rounded border-gray-300 focus:ring-orange-500"
                                    />
                                    <span className="font-medium text-gray-900">Analytics Cookies</span>
                                </label>
                                <p className="text-sm text-gray-600 mt-2 ml-7">
                                    Help us understand how visitors interact with our website (Google Analytics).
                                </p>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-3 justify-end">
                                <button
                                    onClick={() => setShowSettings(false)}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors duration-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={saveCookieSettings}
                                    className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors duration-200"
                                >
                                    Save Preferences
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CookieConsent;